import { useMemo } from "react";
import { patchConnection, type ConnectionPatch } from "../api/chains";
import { useChainStore } from "../store/chainStore";

const FLUSH_DELAY_MS = 400;

interface DirtyEntry {
  chainId: string;
  patch: ConnectionPatch;
}

const dirtyConnections = new Map<string, DirtyEntry>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;

function mergeEntry(connectionId: string, chainId: string, patch: ConnectionPatch) {
  const existing = dirtyConnections.get(connectionId);
  if (existing && existing.chainId === chainId) {
    existing.patch = { ...existing.patch, ...patch };
  } else {
    dirtyConnections.set(connectionId, { chainId, patch: { ...patch } });
  }
}

function flush() {
  if (flushTimer) {
    clearTimeout(flushTimer);
    flushTimer = null;
  }
  if (dirtyConnections.size === 0) return;

  const pending = new Map(dirtyConnections);
  dirtyConnections.clear();

  for (const [connectionId, { chainId, patch }] of pending) {
    patchConnection(chainId, connectionId, patch).catch((err) => {
      console.error("Failed to persist connection", err);
      if (!dirtyConnections.has(connectionId)) {
        dirtyConnections.set(connectionId, { chainId, patch });
      }
      scheduleFlush();
    });
  }
}

function scheduleFlush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flush, FLUSH_DELAY_MS);
}

export interface ConnectionSaver {
  enqueue: (connectionId: string, patch: ConnectionPatch) => void;
}

export function useConnectionSaver(): ConnectionSaver {
  return useMemo<ConnectionSaver>(
    () => ({
      enqueue: (connectionId, patch) => {
        if (Object.keys(patch).length === 0) return;
        const chainId = useChainStore.getState().chainId;
        if (chainId == null) return;
        mergeEntry(connectionId, chainId, patch);
        scheduleFlush();
      },
    }),
    [],
  );
}
